/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Devis\ConfigurateurArticle\components\FinalisationCompact.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - COMPOSANT FINALISATION COMPACTE

📋 DESCRIPTION:
Composant React d'interface compacte de finalisation avec gestion complète du cycle de vie des devis.
Contrôle des transitions de statut et actions commerciales intégrées.

🎯 RESPONSABILITÉS:
- Affichage compact des informations de finalisation avec indicateurs visuels
- Gestion complète du cycle de vie des devis (7 statuts possibles)
- Contrôle des transitions de statut avec validation automatique
- Actions commerciales intégrées (PDF, acceptation, livraison, abandon)
- Interface de validation avec sections obligatoires/facultatives
- Calcul dynamique du statut selon logique backend

🔗 DÉPENDANCES:
- FinalisationModal: Interface détaillée de finalisation
- React: Composant fonctionnel avec gestion d'état
- API Backend: Endpoints de gestion des statuts de devis
- LocalStorage: Authentification et données utilisateur

📡 INTERACTIONS:
- Backend: Transitions de statut via API /api/devis/{id}/marquer-*
- Parent: Communication actions via callbacks (onVoirDetail, onGenererPDF)
- Modal: Ouverture interface détaillée pour complétion
- Utilisateur: Actions commerciales directes (acceptation, livraison)

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Transitions de statut conformes à la spécification backend
- Validation automatique des champs obligatoires
- Interface adaptative selon statut actuel du devis
- Actions commerciales avec confirmations utilisateur
- Rechargement automatique après actions pour synchronisation
*/
import React from 'react';
import { FinalisationData } from './FinalisationModal';
import { apiFetch } from '../../../../services/httpClient';

interface FinalisationCompactProps {
  finalisationData: FinalisationData;
  clientInfo?: any;
  devisExistant?: any;
  onVoirDetail: () => void;
  onGenererPDF?: () => void;
  onMarquerLivre?: () => void;
  isEditMode?: boolean;
  devisModifie?: boolean;
  modeConserve?: boolean;
  hasCgv?: boolean;
  inclureCgv?: boolean;
  onToggleCgv?: (checked: boolean) => void;
}

const FinalisationCompact: React.FC<FinalisationCompactProps> = ({
  finalisationData,
  clientInfo,
  devisExistant,
  onVoirDetail,
  onGenererPDF,
  onMarquerLivre,
  isEditMode,
  devisModifie,
  modeConserve = false,
  hasCgv = false,
  inclureCgv = false,
  onToggleCgv
}) => {
  // Calculer le statut dynamiquement selon la même logique que le backend
  const calculerStatutDynamique = () => {
    // Vérifications par ordre de priorité selon la spécification backend
    if (devisExistant?.marque_comme_livre) {
      return 'TERMINE_LIVRE';
    }
    
    if (devisExistant?.marque_comme_accepte) {
      return 'ACCEPTE';
    }
    
    if (devisExistant?.pdf_genere_le) {
      return 'EN_ATTENTE_RETOUR_CLIENT';
    }
    
    // Utiliser le statut du devis existant pour les autres cas
    return devisExistant?.statut || 'NOUVEAU';
  };
  
  const statutActuel = calculerStatutDynamique();
  // ✅ CORRECTION: Vérifier aussi devisExistant.client car c'est là qu'il est sauvegardé
  const clientPresent = !!(clientInfo || finalisationData.clientId || devisExistant?.client);
  const projetPresent = !!(devisExistant?.projet || finalisationData.nomProjet);
  const datePresente = !!(devisExistant?.date || finalisationData.dateDevis);
  const fpStatut = (finalisationData.fraisPort?.statut || 'INCONNU').toUpperCase();
  
  // Calculer le statut de completion
  const champsObligatoires = [
    clientPresent,
    projetPresent,
    datePresente,
    finalisationData.conditionsPaiement,
    finalisationData.delaisLivraison,
    finalisationData.signatureConfig?.nomSignataire,
    finalisationData.inclureBonPourAccord !== undefined
  ];
  
  const champsRemplis = champsObligatoires.filter(Boolean).length;
  const totalChamps = champsObligatoires.length;
  const estComplet = champsRemplis === totalChamps;
  
  const sectionsStatus = [
    { nom: "Client & Projet", complet: !!(clientPresent && projetPresent && datePresente) },
    { nom: "Conditions paiement", complet: !!finalisationData.conditionsPaiement },
    { nom: "Délais & Validité", complet: !!finalisationData.delaisLivraison },
    { nom: "Mentions libres", complet: true }, // Facultatif
    { nom: "Signature client", complet: !!(finalisationData.signatureConfig?.nomSignataire && finalisationData.inclureBonPourAccord !== undefined) },
    { nom: "Livraison", complet: true }, // Facultatif
    { nom: "Frais déplacement", complet: !finalisationData.fraisDeplacementConfig?.distance || !!finalisationData.fraisDeplacementConfig?.distance }, // Complet si pas de distance OU si distance renseignée
    { nom: "Frais de port", complet: fpStatut !== 'INCONNU' } // Inconnu = non inclus mais non bloquant
  ];

  return (
    <div className="bg-white shadow rounded-lg p-6 mt-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium">Finalisation du devis</h2>
          <p className="text-xs text-gray-600 mt-1">
            Le devis généré reste sous la responsabilité du professionnel émetteur. Vérifiez les montants, TVA, remises, frais et mentions avant transmission.
          </p>
          <div className="text-sm text-gray-600 mt-1">
            <span className={`font-medium ${estComplet ? 'text-green-600' : 'text-orange-600'}`}>
              {estComplet ? 'Informations complètes' : `${champsRemplis}/${totalChamps} champs obligatoires remplis`}
            </span>
            <span className="mx-2">•</span>
            <span>Conditions commerciales et signature</span>
            {/* Affichage du statut et de la date de génération PDF si disponible */}
            {statutActuel && (
              <>
                <span className="mx-2">•</span>
                <span className={`font-medium ${
                  statutActuel === 'COMPLET' ? 'text-green-600' :
                  statutActuel === 'EN_ATTENTE_RETOUR_CLIENT' ? 'text-orange-600' :
                  statutActuel === 'ACCEPTE' ? 'text-blue-600' :
                  statutActuel === 'TERMINE_LIVRE' ? 'text-purple-600' :
                  'text-gray-600'
                }`}>
                  {statutActuel === 'NOUVEAU' && '📄 Nouveau'}
                  {statutActuel === 'BROUILLON' && '📝 Brouillon'}
                  {statutActuel === 'ENREGISTRE' && '💾 Enregistré'}
                  {statutActuel === 'COMPLET' && '✅ Complet'}
                  {statutActuel === 'EN_ATTENTE_RETOUR_CLIENT' && '⏳ En attente retour client'}
                  {statutActuel === 'ACCEPTE' && '✍️ Accepté par le client'}
                  {statutActuel === 'TERMINE_LIVRE' && '🎉 Terminé / Livré'}
                  {statutActuel === 'ANNULE' && '❌ Annulé'}
                  {statutActuel === 'ABANDONNE' && '🚫 Abandonné'}
                </span>
                {devisExistant?.pdf_genere_le && (
                  <span className="text-blue-600">
                    {' '}(PDF généré le {new Date(devisExistant.pdf_genere_le).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })})
                  </span>
                )}
              </>
            )}
          </div>
          
          {/* Indicateurs visuels des sections */}
          <div className="flex flex-wrap gap-2 mt-2">
            {sectionsStatus.map((section, index) => (
              <span
                key={index}
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  section.complet 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {section.complet ? '✓' : '○'} {section.nom}
              </span>
            ))}
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            type="button"
            className={`inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md ${
              modeConserve
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'text-gray-700 bg-white hover:bg-gray-50'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200`}
            onClick={modeConserve ? undefined : onVoirDetail}
            disabled={modeConserve}
            title={modeConserve ? 'Action bloquée : devis en mode lecture seule' : undefined}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {estComplet ? 'Modifier les détails' : 'Compléter les informations'}
          </button>
        </div>
      </div>
      
      {/* Actions de finalisation */}
      {estComplet && (
        <div className="mt-4 flex justify-end space-x-3">
          {onGenererPDF && hasCgv && (
            <label className="flex items-center text-sm text-gray-700 mr-auto">
              <input
                type="checkbox"
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded mr-2"
                checked={!!inclureCgv}
                onChange={(e) => onToggleCgv && onToggleCgv(e.target.checked)}
              />
              Inclure les Conditions Générales de Vente au devis
            </label>
          )}
          {/* Bouton Sauvegarder (en mode édition avec modifications) */}
          {isEditMode && devisModifie && (
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
              onClick={() => {/* TODO: Implémenter sauvegarde automatique */}}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              Sauvegarder les modifications
            </button>
          )}
          
          {/* Bouton Générer PDF */}
          {onGenererPDF && (
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
              onClick={onGenererPDF}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Générer PDF
            </button>
          )}
          
          {/* Bouton Marquer comme accepté - Disponible si EN_ATTENTE_RETOUR_CLIENT, Désactivé si déjà accepté */}
          {(statutActuel === 'EN_ATTENTE_RETOUR_CLIENT' || statutActuel === 'ACCEPTE' || statutActuel === 'TERMINE_LIVRE') && (
            <button
              type="button"
              className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md transition-colors duration-200 ${
                statutActuel === 'EN_ATTENTE_RETOUR_CLIENT' 
                  ? 'border-transparent text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  : 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100 cursor-pointer'
              }`}
              onClick={async () => {
                if (!devisExistant?.id) return;
                
                // Vérifier qu'on ne peut pas annuler l'acceptation si déjà livré
                if (statutActuel === 'TERMINE_LIVRE') {
                  alert('Impossible d\'annuler l\'acceptation d\'un devis déjà livré. Annulez d\'abord la livraison.');
                  return;
                }
                
                try {
                  const userId = JSON.parse(localStorage.getItem('user') || '{}').userId || 'user1';
                  const endpoint = statutActuel === 'EN_ATTENTE_RETOUR_CLIENT'
                    ? `/devis/${devisExistant.id}/marquer-accepte`
                    : `/devis/${devisExistant.id}/annuler-acceptation`;

                  await apiFetch(endpoint, {
                    method: 'POST',
                    body: JSON.stringify({
                      notes_acceptation: statutActuel === 'EN_ATTENTE_RETOUR_CLIENT' ? 'Devis accepte par le client' : ''
                    })
                  });

                  window.location.reload();
                } catch (error) {
                  console.error("Erreur lors de l'action:", error);
                  alert('Erreur lors de l\'action');
                }
              }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {statutActuel === 'EN_ATTENTE_RETOUR_CLIENT' ? 'Marquer comme accepté' : '✓ Accepté par le client'}
            </button>
          )}
          
          {/* Bouton Marquer/Annuler livraison - Toujours cliquable */}
          {(statutActuel === 'ACCEPTE' || statutActuel === 'TERMINE_LIVRE') && (
            <button
              type="button"
              className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md transition-colors duration-200 ${
                statutActuel === 'ACCEPTE' 
                  ? 'border-transparent text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                  : 'border-purple-300 text-purple-700 bg-purple-50 hover:bg-purple-100 cursor-pointer'
              }`}
              onClick={async () => {
                if (!devisExistant?.id) return;
                
                let confirmer, endpoint, action;
                const userId = JSON.parse(localStorage.getItem('user') || '{}').userId || 'user1';
                if (statutActuel === 'ACCEPTE') {
                  confirmer = window.confirm('Marquer ce devis comme livré ?');
                  endpoint = `/devis/${devisExistant.id}/marquer-livre`;
                  action = 'marquage comme livré';
                } else {
                  confirmer = window.confirm('Annuler la livraison de ce devis ?');
                  endpoint = `/devis/${devisExistant.id}/annuler-livraison`;
                  action = 'annulation de la livraison';
                }
                
                if (!confirmer) return;
                
                try {
                  await apiFetch(endpoint, {
                    method: 'POST',
                    body: JSON.stringify({
                      notes_livraison: statutActuel === 'ACCEPTE' ? 'Devis livre avec succes' : ''
                    })
                  });
                  
                  window.location.reload();
                } catch (error) {
                  console.error(`Erreur lors du ${action}:`, error);
                  alert(`Erreur lors du ${action}`);
                }
              }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              {statutActuel === 'ACCEPTE' ? 'Marquer comme livré' : '🎉 Terminé / Livré'}
            </button>
          )}
          
          {/* Bouton Marquer comme abandonné - Disponible pour tous les statuts sauf TERMINE_LIVRE */}
          {statutActuel !== 'TERMINE_LIVRE' && statutActuel !== 'ABANDONNE' && (
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
              onClick={async () => {
                if (!devisExistant?.id) return;
                
                const raison = window.prompt('Raison de l\'abandon du projet (optionnel):');
                if (raison === null) return; // Annulé par l'utilisateur
                
                const confirmer = window.confirm('Marquer ce devis comme abandonné ? Cette action indique que le projet a été abandonné par le client.');
                if (!confirmer) return;
                
                try {
                  const userId = JSON.parse(localStorage.getItem('user') || '{}').userId || 'user1';
                  await apiFetch(`/devis/${devisExistant.id}/marquer-abandonne`, {
                    method: 'POST',
                    body: JSON.stringify({
                      raison_abandon: raison || 'Projet abandonne par le client'
                    })
                  });
                  
                  window.location.reload();
                } catch (error) {
                  console.error('Erreur lors du marquage comme abandonne:', error);
                  alert('Erreur lors du marquage comme abandonne');
                }
              }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Marquer comme abandonné
            </button>
          )}
        </div>
      )}

      {!estComplet && (
        <div className="mt-4 p-3 bg-orange-50 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-orange-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span className="text-sm text-orange-700">
              Veuillez compléter les informations obligatoires pour finaliser le devis.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinalisationCompact;
